import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Tab,
  Tabs,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@wc/domain/authn";
import { PermissionsEnum, userHasPermissionInOrg } from "@wc/domain/authz";
import { listOrgMembers } from "@wc/domain/org-membership";
import { addTeamMember, listTeamMembers } from "@wc/domain/team-membership";

import { createTeamInvite, INVITE_DEFAULTS } from "../index";

import InviteLinkDisplay from "./invite-link-display";
import TeamInvitesList from "./team-invites-list";

/**
 * @param {object} props Component props
 * @param {boolean} props.isOpen Whether modal is open
 * @param {(isOpen: boolean) => void} props.onOpenChange Modal open state handler
 * @param {string} props.teamId The team ID
 * @param {string} props.organizationId The organization ID
 * @param {() => void} [props.onSuccess] Callback when member added successfully
 * @returns {React.ReactElement} Add team member modal
 */
export default function AddTeamMemberModal({
  isOpen,
  onOpenChange,
  teamId,
  organizationId,
  onSuccess,
}) {
  const { t } = useTranslation();
  const userData = useAuth();
  const [activeTab, setActiveTab] = React.useState("existing");
  const [canInvite, setCanInvite] = React.useState(false);
  const [availableMembers, setAvailableMembers] = React.useState([]);
  const [selectedMember, setSelectedMember] = React.useState(new Set([]));
  const [isLoading, setIsLoading] = React.useState(true);
  const [isAdding, setIsAdding] = React.useState(false);
  const [createdInvite, setCreatedInvite] = React.useState(null);
  const [isCreatingInvite, setIsCreatingInvite] = React.useState(false);

  React.useEffect(() => {
    const loadData = async () => {
      if (!isOpen || !teamId || !organizationId) return;

      setIsLoading(true);
      setCreatedInvite(null);

      try {
        const hasInvitePerm = await userHasPermissionInOrg(
          userData.getCurrentUser(),
          organizationId,
          PermissionsEnum.INVITE_ORGANIZATION_MEMBER,
        );
        setCanInvite(hasInvitePerm);

        const [orgMembers, teamMembers] = await Promise.all([
          listOrgMembers(organizationId, userData.getCurrentUser().id),
          listTeamMembers(teamId, userData.getCurrentUser().id),
        ]);

        const teamMemberIds = new Set(teamMembers.map((m) => m.id));
        const available = orgMembers.filter((m) => !teamMemberIds.has(m.id));
        setAvailableMembers(available);
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isOpen, teamId, organizationId, userData]);

  const handleAddMember = async () => {
    if (selectedMember.size === 0) return;

    const memberId = Array.from(selectedMember)[0];
    setIsAdding(true);

    try {
      await addTeamMember(teamId, memberId, userData.getCurrentUser());
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to add member:", error);
    } finally {
      setIsAdding(false);
    }
  };

  const handleCreateInvite = async () => {
    setIsCreatingInvite(true);

    try {
      const token = await createTeamInvite(
        { teamId, organizationId },
        userData.getCurrentUser(),
      );

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + INVITE_DEFAULTS.EXPIRATION_DAYS);

      setCreatedInvite({ token, expiresAt });
    } catch (error) {
      console.error("Failed to create invite:", error);
    } finally {
      setIsCreatingInvite(false);
    }
  };

  const handleClose = () => {
    setSelectedMember(new Set([]));
    setCreatedInvite(null);
    setActiveTab("existing");
    onOpenChange(false);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose} size="2xl">
      <ModalContent>
        <ModalHeader>{t("invites.addMember")}</ModalHeader>
        <ModalBody>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <Tabs
              selectedKey={activeTab}
              onSelectionChange={setActiveTab}
              fullWidth
            >
              <Tab key="existing" title={t("invites.addExisting")}>
                <div className="space-y-4 pt-4">
                  {availableMembers.length === 0 ? (
                    <p className="text-default-400 text-center">
                      {t("invites.noAvailableMembers")}
                    </p>
                  ) : (
                    <Select
                      label={t("invites.selectMember")}
                      selectedKeys={selectedMember}
                      onSelectionChange={setSelectedMember}
                      aria-label="Select member"
                    >
                      {availableMembers.map((member) => (
                        <SelectItem
                          key={member.id}
                          textValue={member.displayName}
                        >
                          {member.displayName}
                        </SelectItem>
                      ))}
                    </Select>
                  )}
                </div>
              </Tab>
              {canInvite && (
                <Tab key="invite" title={t("invites.createLink")}>
                  <div className="space-y-4 pt-4">
                    {createdInvite ? (
                      <InviteLinkDisplay
                        token={createdInvite.token}
                        expiresAt={createdInvite.expiresAt}
                      />
                    ) : (
                      <div className="text-center">
                        <p className="text-default-500 mb-4">
                          {t("invites.createLinkDescription")}
                        </p>
                        <Button
                          color="primary"
                          onPress={handleCreateInvite}
                          isLoading={isCreatingInvite}
                        >
                          {t("invites.generateLink")}
                        </Button>
                      </div>
                    )}

                    <div className="border-divider mt-6 border-t pt-4">
                      <h4 className="mb-2 text-sm font-medium">
                        {t("invites.activeInvites")}
                      </h4>
                      <TeamInvitesList
                        teamId={teamId}
                        organizationId={organizationId}
                      />
                    </div>
                  </div>
                </Tab>
              )}
            </Tabs>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={handleClose}>
            {t("cancel")}
          </Button>
          {activeTab === "existing" && availableMembers.length > 0 && (
            <Button
              color="primary"
              onPress={handleAddMember}
              isLoading={isAdding}
              isDisabled={selectedMember.size === 0}
            >
              {t("invites.addToTeam")}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
